import {
  MaintenanceWindowPreviewPatchManagementWebComponentSchema,
  POSTPONE_REBOOT_TYPE,
  PostponeReboot,
} from '../../../../../../../maintenanceWindows/maintenanceWindowPreviewPatchManagement/types';
import { NewScheduler, OldScheduler } from '../../../../../../../types/common/maintenanceWindowPatchManagementTypes';
import { padHoursAndMinutes } from '../../../../../../../utils/padHoursAndMinutes.utils';
import { OldSchedulerHelperV2 } from './OldSchedulerHelperV2';
import { NewSchedulerHelperV2 } from './NewSchedulerHelperV2';

export interface MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2 {
  targetedOperations: string;
  patchScope: string;
  recurrence: string;
  reboot?: string;
}
export interface SummaryData {
  name: string;
  previews: MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2[];
}

export class MaintenanceWindowPreviewConverterV2 {
  protected dependencies: any;

  injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
  }

  /**
   * Convert data for maintenance window preview.
   * @param data
   */
  public convertToTemplateDataV2(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema): SummaryData {
    // array of previews
    const previews: MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2[] = [];
    // name of maintenance window
    const name = data.name;
    // enabled operations
    const isScanPatchEnabled = data.specifics?.scanPatchesSettings?.enabled;
    const isInstallPatchEnabled = data.specifics?.installPatchesSettings?.enabled;
    const isSecurityPatchEnabled = data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.enabled;
    const isNonSecurityPatchEnabled =
      data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.enabled;
    const isManuallyApprovedPatchesSettingsEnabled =
      data.specifics?.installPatchesSettings?.updateType?.manuallyApprovedPatchesSettings?.enabled;
    // scheduler settings - switch between old and new scheduler
    let schedulerToConvert: OldScheduler | NewScheduler;
    const useFallback = data.specifics?.scanPatchesSettings?.useFallbackSchedule;
    const useSameScheduler = data.specifics?.scanPatchesSettings?.useSameSchedule;
    const helper = useFallback ? new OldSchedulerHelperV2() : new NewSchedulerHelperV2();
    helper.injectDependencies(this.dependencies);

    // scan for patches
    schedulerToConvert = useFallback
      ? data.specifics?.scanPatchesSettings?.oldScheduler
      : data.specifics?.scanPatchesSettings?.scheduler;
    previews.push(
      this.getMaintenanceWindowPreviewV2(
        { scan: isScanPatchEnabled, install: false },
        {
          security: isScanPatchEnabled,
          nonSecurity: isScanPatchEnabled,
          manuallyApproved: false,
        }, // scan for patches is always security and non-security
        {
          enabled: isScanPatchEnabled, // recurrence is set only if scan for patches is enabled
          useFallback: useFallback,
          schedulerHelper: helper,
          schedulerToConvert: schedulerToConvert,
        },
        { enabled: false } // reboot is always hidden for scan for patches
      )
    );
    if (!isInstallPatchEnabled) {
      return { name: name, previews: previews };
    }

    // apply security patches
    if (!useSameScheduler) {
      schedulerToConvert = useFallback
        ? data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.oldScheduler
        : data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.scheduler;
    }
    if (isSecurityPatchEnabled) {
      previews.push(
        this.getMaintenanceWindowPreviewV2(
          { scan: false, install: isInstallPatchEnabled },
          { security: isSecurityPatchEnabled, nonSecurity: false, manuallyApproved: false }, // only security should be set
          {
            enabled: isSecurityPatchEnabled, // recurrence is set only if security patch is enabled
            useFallback: useFallback,
            schedulerHelper: helper,
            schedulerToConvert: schedulerToConvert,
          },
          { enabled: true, postponeReboot: data.specifics?.installPatchesSettings?.postponeReboot } // reboot should be set for security patches
        )
      );
    }

    // apply non-security patches
    if (!useSameScheduler) {
      schedulerToConvert = useFallback
        ? data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.oldScheduler
        : data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.scheduler;
    }
    if (isNonSecurityPatchEnabled) {
      previews.push(
        this.getMaintenanceWindowPreviewV2(
          { scan: false, install: isInstallPatchEnabled },
          { security: false, nonSecurity: isNonSecurityPatchEnabled, manuallyApproved: false }, // only non-security should be set
          {
            enabled: isNonSecurityPatchEnabled, // recurrence is set only if non-security patch is enabled
            useFallback: useFallback,
            schedulerHelper: helper,
            schedulerToConvert: schedulerToConvert,
          },
          { enabled: true, postponeReboot: data.specifics?.installPatchesSettings?.postponeReboot } // reboot should be set for non-security patches
        )
      );
    }

    // apply manually approved patches
    if (!useSameScheduler) {
      const securitySchedulerToConvert = useFallback
        ? data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.oldScheduler
        : data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.scheduler;
      const manuallyApprovedSchedulerToConvert = useFallback
        ? data.specifics?.installPatchesSettings?.updateType?.manuallyApprovedPatchesSettings?.oldScheduler
        : data.specifics?.installPatchesSettings?.updateType?.manuallyApprovedPatchesSettings?.scheduler;

      // if security patches are enabled, use security scheduler, otherwise use manually approved scheduler
      schedulerToConvert = isSecurityPatchEnabled ? securitySchedulerToConvert : manuallyApprovedSchedulerToConvert;
    }
    if (isManuallyApprovedPatchesSettingsEnabled) {
      previews.push(
        this.getMaintenanceWindowPreviewV2(
          { scan: false, install: isInstallPatchEnabled },
          { security: false, nonSecurity: false, manuallyApproved: isManuallyApprovedPatchesSettingsEnabled }, // only manually approved should be set
          {
            enabled: isManuallyApprovedPatchesSettingsEnabled, // recurrence is set only if manually approved patch is enabled
            useFallback: useFallback,
            schedulerHelper: helper,
            schedulerToConvert: schedulerToConvert,
          },
          { enabled: true, postponeReboot: data.specifics?.installPatchesSettings?.postponeReboot } // reboot should be set for non-security patches
        )
      );
    }

    return { name: name, previews: previews };
  }

  /**
   * Get maintenance window preview.
   * @param targetedOperations specifies if scan and/or install patches should be set
   * @param patchScope specifies if security and/or non-security patches should be set
   * @param recurrence specifies if recurrence should be set and specifies scheduler settings
   * @param reboot specifies if reboot should be set and specifies reboot settings
   */
  private getMaintenanceWindowPreviewV2(
    targetedOperations: { scan: boolean; install: boolean },
    patchScope: { security: boolean; nonSecurity: boolean; manuallyApproved: boolean },
    recurrence: {
      enabled: boolean;
      useFallback: boolean;
      schedulerHelper: OldSchedulerHelperV2 | NewSchedulerHelperV2;
      schedulerToConvert: OldScheduler | NewScheduler;
    },
    reboot: { enabled: boolean; postponeReboot?: PostponeReboot }
  ): MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2 {
    const preview: MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2 = {
      targetedOperations: this.getTargetedOperationsV2(targetedOperations.scan, targetedOperations.install),
      patchScope: this.getPatchScopeV2(patchScope.security, patchScope.nonSecurity, patchScope.manuallyApproved),
      recurrence: this.getRecurrencesV2(
        recurrence.enabled,
        recurrence.useFallback,
        recurrence.schedulerHelper,
        recurrence.schedulerToConvert
      ),
    };

    if (reboot.enabled) {
      preview.reboot = this.getRebootV2(reboot.postponeReboot);
    }

    return preview;
  }

  /**
   * Get targeted operations.
   * @param scan specifies if scan for patches should be set
   * @param install specifies if apply patches should be set
   */
  private getTargetedOperationsV2(scan: boolean, install: boolean) {
    const targetedOperationsArr: string[] = [];

    if (scan) {
      targetedOperationsArr.push(this.dependencies.translateFn('Scan for patches'));
    }

    if (install) {
      targetedOperationsArr.push(this.dependencies.translateFn('Scan and apply patches'));
    }

    // no targeted operation available
    if (!targetedOperationsArr.length) {
      return this.dependencies.translateFn('None');
    }

    return targetedOperationsArr.join(', ');
  }

  /**
   * Get patch scope.
   * @param security specifies if security patches should be set
   * @param nonSecurity specifies if non-security patches should be set
   * @param manuallyApproved specifies if manually approved patches should be set
   */
  private getPatchScopeV2(security: boolean, nonSecurity: boolean, manuallyApproved: boolean) {
    const patchScopeArr: string[] = [];

    if (security) {
      patchScopeArr.push(this.dependencies.translateFn('Security'));
    }

    if (nonSecurity) {
      patchScopeArr.push(this.dependencies.translateFn('Non-security'));
    }

    if (manuallyApproved) {
      patchScopeArr.push(this.dependencies.translateFn('Manually approved'));
    }

    // no patch scope available
    if (!patchScopeArr.length) {
      return this.dependencies.translateFn('None');
    }

    return patchScopeArr.join(', ');
  }

  /**
   * Get recurrences.
   * @param enabled specifies if recurrence should be set
   * @param useFallback specifies if fallback schedule should be used
   * @param schedulerHelper specifies scheduler helper (old/new version)
   * @param schedulerToConvert specifies scheduler to convert (old/new version)
   */
  private getRecurrencesV2(
    enabled: boolean,
    useFallback: boolean,
    schedulerHelper: OldSchedulerHelperV2 | NewSchedulerHelperV2,
    schedulerToConvert: OldScheduler | NewScheduler
  ) {
    if (!enabled) {
      return this.dependencies.translateFn('Disabled');
    }

    // use old scheduler
    if (useFallback) {
      return (schedulerHelper as OldSchedulerHelperV2).getShortDescription(schedulerToConvert as OldScheduler);
    }

    // use new scheduler
    return (schedulerHelper as NewSchedulerHelperV2).getShortDescription(schedulerToConvert as NewScheduler);
  }

  /**
   * Get reboot.
   * @param postponeReboot specifies reboot settings
   */
  private getRebootV2(postponeReboot?: PostponeReboot) {
    let description = this.dependencies.translateFn('N/A');

    if (!postponeReboot) {
      return description;
    }

    const rebootOption = postponeReboot.option;
    switch (rebootOption) {
      case POSTPONE_REBOOT_TYPE.TIME:
        description = this.dependencies.translateFn('Users can postpone the reboot until a more convenient time');

        break;
      case POSTPONE_REBOOT_TYPE.BETWEEN:
        description = this.dependencies.translateFn(
          'Users can postpone the reboot only between {startTime} and {endTime}',
          {
            startTime: padHoursAndMinutes`${postponeReboot?.between?.startHour}:${postponeReboot?.between?.startMinute}`,
            endTime: padHoursAndMinutes`${postponeReboot?.between?.endHour}:${postponeReboot?.between?.endMinute}`,
          }
        );

        break;
      case POSTPONE_REBOOT_TYPE.AFTER:
        description = this.dependencies.translateFn('System will reboot automatically after {afterMinutes} minutes', {
          afterMinutes: postponeReboot?.afterMinutes,
        });

        break;
      default:
        break;
    }

    return description;
  }
}
